<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const canvasRef = ref(null)
let ctx = null
let animationFrameId = null
let width = 0
let height = 0
let isDay = false
let observer = null

const mouse = {
  x: 0,
  y: 0,
}

// --- Arrays for all elements ---
let insects = [] // Butterflies / Fireflies
let stars = [] // Night BG
let shootingStars = [] // Night BG
let leaves = [] // Day BG
let clouds = [] // Day BG

// --- Config ---
// Insects
const INSECT_COUNT = 12

// Night BG
const STAR_COUNT = 150
let nextMeteorFrame = Math.random() * 600 + 200

// Day BG
const LEAF_COUNT = 30
const CLOUD_COUNT = 6

// --- UTILS ---
const drawSprite = (ctx, x, y, size, colorMap, spriteGrid) => {
  const pixelSize = size
  spriteGrid.forEach((row, rowIndex) => {
    row.forEach((col, colIndex) => {
      if (col !== 0 && colorMap[col]) {
        ctx.fillStyle = colorMap[col]
        ctx.fillRect(x + colIndex * pixelSize, y + rowIndex * pixelSize, pixelSize, pixelSize)
      }
    })
  })
}

// --- SPRITES (Insects) ---
const butterflyColors = {
  1: '#313132', // Cuerpo
  2: '#38a7e7', // Alas
  3: '#fff', // Detalles
}

const butterflyOpen = [
  [2, 2, 1, 2, 2],
  [2, 3, 1, 3, 2],
  [0, 2, 1, 2, 0],
  [0, 0, 1, 0, 0],
]

const butterflyClosed = [
  [0, 2, 1, 2, 0],
  [0, 2, 1, 2, 0],
  [0, 0, 1, 0, 0],
  [0, 0, 1, 0, 0],
]

const fireflySpriteDesign = [
  [0, 1, 1, 0],
  [2, 1, 1, 2],
  [0, 3, 3, 0],
]

// --- CLASSES ---

/* ===========================
   INSECT (Butterfly / Firefly)
   =========================== */
class Insect {
  constructor() {
    this.init()
  }

  init() {
    this.x = Math.random() * width
    this.y = Math.random() * height
    this.vx = Math.random() * 2 - 1
    this.vy = Math.random() * 2 - 1
    this.size = isDay ? 3 : 4

    this.angle = Math.random() * Math.PI * 2
    this.wanderNoise = Math.random() * 100

    this.wingState = 0
    this.glowIntensity = Math.random()
    this.glowSpeed = 0.03 + Math.random() * 0.05

    if (isDay) {
      this.baseSpeed = 2 + Math.random()
      this.burstSpeed = this.baseSpeed
      this.turnSpeed = 0.1
    } else {
      this.baseSpeed = 1.0 + Math.random() * 0.5
      this.burstSpeed = 5.0 + Math.random() * 2.0
      this.turnSpeed = 0.15
    }
  }

  update() {
    this.wanderNoise += 0.1
    this.angle += (Math.sin(this.wanderNoise) * 2 - 1) * this.turnSpeed

    const dx = mouse.x - this.x
    const dy = mouse.y - this.y
    const dist = Math.sqrt(dx * dx + dy * dy)

    let currentMaxSpeed = this.baseSpeed
    let attractionStrength = 0

    if (isDay) {
      currentMaxSpeed = this.baseSpeed
      if (dist > 100) attractionStrength = 0.06
      else attractionStrength = 0.02
    } else {
      if (dist > 150) {
        currentMaxSpeed = this.burstSpeed
        attractionStrength = 0.08
      } else {
        currentMaxSpeed = this.baseSpeed
        attractionStrength = 0.005
        if (dist < 30) attractionStrength = -0.01
      }
    }

    let targetVx = Math.cos(this.angle) * currentMaxSpeed
    let targetVy = Math.sin(this.angle) * currentMaxSpeed

    targetVx += (dx / dist) * attractionStrength * 50
    targetVy += (dy / dist) * attractionStrength * 50

    const inertia = isDay ? 0.95 : 0.92
    this.vx = this.vx * inertia + targetVx * (1 - inertia)
    this.vy = this.vy * inertia + targetVy * (1 - inertia)

    this.x += this.vx
    this.y += this.vy
    this.y += Math.sin(Date.now() / 200 + this.wanderNoise) * 0.5

    const margin = 50
    if (this.x < -margin) this.x = width + margin
    if (this.x > width + margin) this.x = -margin
    if (this.y < -margin) this.y = height + margin
    if (this.y > height + margin) this.y = -margin

    if (isDay) {
      this.wingState = Math.sin(Date.now() / 60 + this.wanderNoise * 10) > 0 ? 'open' : 'closed'
    } else {
      this.glowIntensity += this.glowSpeed
      if (this.glowIntensity > 1.5 || this.glowIntensity < -0.5) this.glowSpeed *= -1
    }
  }

  draw() {
    ctx.save()
    if (isDay) {
      ctx.fillStyle = 'rgba(0,0,0,0.1)'
      ctx.fillRect(this.x + 5, this.y + 15, 8, 4)
      const sprite = this.wingState === 'open' ? butterflyOpen : butterflyClosed
      const tilt = this.vx * 0.2
      ctx.translate(this.x, this.y)
      ctx.rotate(tilt)
      ctx.translate(-(sprite[0].length * this.size) / 2, -(sprite.length * this.size) / 2)
      drawSprite(ctx, 0, 0, this.size, butterflyColors, sprite)
    } else {
      let glow = Math.max(0, Math.min(1, this.glowIntensity))
      const fireflyColors = {
        1: '#1e293b',
        2: '#334155',
        3: `rgba(173, 255, 47, ${glow})`,
      }
      ctx.translate(this.x, this.y)
      if (glow > 0.1) {
        ctx.shadowBlur = 20 * glow
        ctx.shadowColor = `rgba(173, 255, 47, ${glow})`
      }
      const spriteW = fireflySpriteDesign[0].length * this.size
      const spriteH = fireflySpriteDesign.length * this.size
      drawSprite(ctx, -spriteW / 2, -spriteH / 2, this.size, fireflyColors, fireflySpriteDesign)
      if (glow > 0.8) {
        ctx.shadowBlur = 0
        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
        ctx.fillRect(-2, 2, 4, 4)
      }
    }
    ctx.restore()
  }
}

/* ===========================
   NIGHT BG ELEMENTS
   =========================== */
class PixelStar {
  constructor() {
    this.init()
  }
  init() {
    this.x = Math.floor(Math.random() * width)
    this.y = Math.floor(Math.random() * height)
    this.size = Math.random() < 0.8 ? 2 : 3
    this.baseAlpha = Math.random() * 0.4 + 0.2
    this.alpha = this.baseAlpha
    this.blinkSpeed = Math.random() * 0.003 + 0.001
    this.blinkDir = 1
  }
  update() {
    if (this.blinkDir === 1) {
      this.alpha += this.blinkSpeed
      if (this.alpha >= this.baseAlpha + 0.15) this.blinkDir = -1
    } else {
      this.alpha -= this.blinkSpeed
      if (this.alpha <= this.baseAlpha - 0.15) this.blinkDir = 1
    }
    this.alpha = Math.max(0.1, Math.min(0.8, this.alpha))
  }
  draw() {
    ctx.fillStyle = `rgba(255, 255, 255, ${this.alpha})`
    ctx.fillRect(this.x, this.y, this.size, this.size)
  }
}

class ShootingStar {
  constructor() {
    this.reset()
  }
  reset() {
    this.x = Math.random() * width
    this.y = Math.random() * (height * 0.7)
    this.len = Math.random() * 60 + 20
    this.speed = Math.random() * 4 + 2
    this.size = 2
    this.angle = 25 + Math.random() * 15
    this.life = 0
    this.maxLife = 80
    this.opacity = 0
  }
  update() {
    this.x += this.speed * Math.cos((this.angle * Math.PI) / 180)
    this.y += this.speed * Math.sin((this.angle * Math.PI) / 180)
    this.life++
    if (this.life < 10) this.opacity += 0.1
    else if (this.life > this.maxLife - 15) this.opacity -= 0.1
    if (this.x > width || this.y > height || this.opacity <= 0) return false
    return true
  }
  draw() {
    const tailX = this.x - this.len * Math.cos((this.angle * Math.PI) / 180)
    const tailY = this.y - this.len * Math.sin((this.angle * Math.PI) / 180)
    const gradient = ctx.createLinearGradient(this.x, this.y, tailX, tailY)
    gradient.addColorStop(0, `rgba(255, 255, 255, ${this.opacity})`)
    gradient.addColorStop(1, `rgba(255, 255, 255, 0)`)
    ctx.lineWidth = this.size
    ctx.strokeStyle = gradient
    ctx.beginPath()
    ctx.moveTo(Math.floor(this.x), Math.floor(this.y))
    ctx.lineTo(Math.floor(tailX), Math.floor(tailY))
    ctx.stroke()
    ctx.fillStyle = `rgba(255, 255, 255, ${this.opacity})`
    ctx.fillRect(Math.floor(this.x), Math.floor(this.y), this.size, this.size)
  }
}

/* ===========================
   DAY BG ELEMENTS
   =========================== */
class PixelCloud {
  constructor() {
    this.reset()
    this.x = Math.random() * width
  }
  reset() {
    this.x = -300
    this.y = Math.random() * (height * 0.4)
    this.speed = Math.random() * 0.3 + 0.1
    this.scale = Math.floor(Math.random() * 3 + 3)
    this.opacity = Math.random() * 0.4 + 0.2
  }
  update() {
    this.x += this.speed
    if (this.x > width + 300) this.reset()
  }
  draw() {
    ctx.save()
    ctx.translate(Math.floor(this.x), Math.floor(this.y))
    ctx.scale(this.scale, this.scale)
    ctx.fillStyle = `rgba(255, 255, 255, ${this.opacity})`
    ctx.fillRect(10, 0, 40, 10)
    ctx.fillRect(0, 10, 60, 10)
    ctx.fillRect(5, 20, 50, 5)
    ctx.restore()
  }
}

class PixelLeaf {
  constructor() {
    this.reset()
  }
  reset() {
    this.x = Math.random() * width
    this.y = -20 - Math.random() * 100
    this.size = Math.floor(Math.random() * 3 + 2)
    this.speedY = Math.random() * 1 + 0.5
    this.speedX = Math.random() * 0.5 - 0.25
    this.sway = Math.random() * Math.PI * 2
    this.swaySpeed = Math.random() * 0.05 + 0.02
    const colors = ['#e67e22', '#d35400', '#27ae60', '#f1c40f']
    this.color = colors[Math.floor(Math.random() * colors.length)]
  }
  update() {
    this.y += this.speedY
    this.sway += this.swaySpeed
    this.x += Math.sin(this.sway) * 0.5
    if (this.y > height + 20) this.reset()
  }
  draw() {
    ctx.fillStyle = this.color
    const s = this.size
    const px = Math.floor(this.x)
    const py = Math.floor(this.y)
    ctx.fillRect(px, py, s, s)
    ctx.fillRect(px - s, py + s, s * 3, s)
    ctx.fillRect(px, py + s * 2, s, s)
  }
}

// --- INIT FUNCTIONS ---
const initInsects = () => {
  insects = []
  for (let i = 0; i < INSECT_COUNT; i++) insects.push(new Insect())
}
const initStars = () => {
  stars = []
  for (let i = 0; i < STAR_COUNT; i++) stars.push(new PixelStar())
}
const initLeaves = () => {
  leaves = []
  for (let i = 0; i < LEAF_COUNT; i++) leaves.push(new PixelLeaf())
}
const initClouds = () => {
  clouds = []
  for (let i = 0; i < CLOUD_COUNT; i++) clouds.push(new PixelCloud())
}

const animate = () => {
  ctx.clearRect(0, 0, width, height)

  // 1. Draw Background Elements first
  if (isDay) {
    clouds.forEach((c) => {
      c.update()
      c.draw()
    })
    leaves.forEach((l) => {
      l.update()
      l.draw()
    })
  } else {
    stars.forEach((s) => {
      s.update()
      s.draw()
    })
    nextMeteorFrame--
    if (nextMeteorFrame <= 0) {
      shootingStars.push(new ShootingStar())
      nextMeteorFrame = Math.random() * 900 + 300
    }
    for (let i = shootingStars.length - 1; i >= 0; i--) {
      const active = shootingStars[i].update()
      shootingStars[i].draw()
      if (!active) shootingStars.splice(i, 1)
    }
  }

  // 2. Draw Insects (Butterflies/Fireflies) on top
  insects.forEach((insect) => {
    insect.update()
    insect.draw()
  })

  animationFrameId = requestAnimationFrame(animate)
}

const checkTheme = () => {
  // .dark = NIGHT (fireflies/stars)
  // no .dark = DAY (butterflies/clouds)
  const isDark = document.documentElement.classList.contains('dark')
  const newIsDay = !isDark

  if (isDay !== newIsDay) {
    isDay = newIsDay

    // Reset appropriate arrays
    if (!isDay) {
      // Switching to NIGHT
      initStars()
    } else {
      // Switching to DAY
      initLeaves()
      initClouds()
    }
    // Re-init insects to change species
    initInsects()
  }
}

const resize = () => {
  if (canvasRef.value) {
    width = window.innerWidth
    height = window.innerHeight
    canvasRef.value.width = width
    canvasRef.value.height = height
    ctx.imageSmoothingEnabled = false

    if (!isDay) initStars()
    else {
      initLeaves()
      initClouds()
    }

    // Don't necessarily kill insects on resize, just let them float from new bounds?
    // Or simpler: re-init to avoid lost particles.
    initInsects()
  }
}

const handleMouseMove = (e) => {
  mouse.x = e.clientX
  mouse.y = e.clientY
}

// --- EXTERNAL CONTROL ---
const setInteractionPoint = (x, y) => {
  mouse.x = x
  mouse.y = y
}

defineExpose({
  setInteractionPoint,
})

onMounted(() => {
  const canvas = canvasRef.value
  ctx = canvas.getContext('2d')

  // Initial dimensions
  resize()

  // Initial Check
  checkTheme()

  // Ensure we have stuff if checkTheme didn't swap
  if (!isDay && stars.length === 0) initStars()
  if (isDay && clouds.length === 0) {
    initLeaves()
    initClouds()
  }
  if (insects.length === 0) initInsects()

  window.addEventListener('resize', resize)
  window.addEventListener('mousemove', handleMouseMove)

  observer = new MutationObserver(checkTheme)
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  })

  animate()
})

onUnmounted(() => {
  cancelAnimationFrame(animationFrameId)
  window.removeEventListener('resize', resize)
  window.removeEventListener('mousemove', handleMouseMove)
  if (observer) observer.disconnect()
})
</script>

<template>
  <div class="bioma-container">
    <!-- Optional Vignette for Atmosphere -->
    <div class="vignette" :class="{ 'vignette--day': isDay, 'vignette--night': !isDay }"></div>
    <canvas ref="canvasRef" class="bioma-canvas"></canvas>
  </div>
</template>

<style scoped>
.bioma-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 50;
}

.bioma-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  image-rendering: pixelated;
}

.vignette {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  transition: background 1.5s ease-in-out;
}

.vignette--day {
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0) 50%,
    rgba(255, 255, 210, 0.1) 80%,
    rgba(255, 255, 255, 0.2) 100%
  );
}

.vignette--night {
  background: radial-gradient(
    circle,
    rgba(0, 0, 0, 0) 30%,
    rgba(0, 0, 10, 0.4) 70%,
    rgba(0, 0, 15, 0.8) 100%
  );
}
</style>
